import { Injectable, OnDestroy } from '@angular/core';
import { UserManager, User, Log, SigninRequest } from 'oidc-client';
import { Role } from '../shared/models/role.model';
import { Router } from '@angular/router';
import { ReplaySubject, Subscription } from 'rxjs';
import { ConfigService } from './config.service';
import { Company } from 'identity-webapi-client';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  public manager: UserManager;
  private user: User | null = null;

  // Token renewal management properties
  private renewalInProgress = false;
  private renewalAttempts = 0;
  private maxRenewalAttempts = 3;
  private renewalTimer: any = null;
  
  // Static properties for circuit breaker
  private static lastTokenRequestTime = 0;
  private static tokenRequestCount = 0;
  private static readonly MIN_REQUEST_INTERVAL = 5000; // 5 seconds minimum between requests

  public userSubject = new ReplaySubject<User | null>(1);
  private userSubscription: Subscription;

  constructor(private router: Router,
    private configService: ConfigService) {
    Log.logger = console;
    Log.level = Log.INFO; // Set to INFO for better debugging
    
    // Get settings but ensure automaticSilentRenew is disabled
    const settings = {
      ...configService.config.userManagerSettings,
      automaticSilentRenew: false // Explicitly disable automatic renewal
    };

    this.manager = new UserManager(settings);
    this.userSubscription = this.userSubject.subscribe(user => {
      this.user = user;
    });

    // Set up proper event handling
    this.setupEvents();

    // Get the initial user
    this.manager.getUser().then(user => {
      this.userSubject.next(user);
    });
  }

  // Set up event handlers for token-related events
  private setupEvents(): void {
    // Handle user loaded event
    this.manager.events.addUserLoaded((user) => {
      //console.log('User loaded event');
      this.userSubject.next(user);
    });

    // Handle silent renew errors - CRITICAL for preventing loops
    this.manager.events.addSilentRenewError((err) => {
      console.error('Silent renew error', err);
      this.renewalInProgress = false;
      
      // In oidc-client, the error object structure might vary
      // Look for login_required in different possible locations
      const errorMessage = 
        (err as any)?.error || 
        (err as any)?.message || 
        (typeof err === 'string' ? err : '');
      
      // Don't retry for login_required - that's normal session expiration
      if (errorMessage.includes('login_required')) {
        //console.log('Session expired, user needs to log in again');
        return;
      }
      
      // For other errors, retry with exponential backoff
      if (this.renewalAttempts < this.maxRenewalAttempts) {
        const delay = Math.pow(2, this.renewalAttempts) * 1000;
        this.renewalAttempts++;
        
        //console.log(`Will retry token renewal in ${delay/1000} seconds (attempt ${this.renewalAttempts})`);
        setTimeout(() => {
          this.signInSilent();
        }, delay);
      } else {
        console.warn(`Maximum renewal attempts (${this.maxRenewalAttempts}) reached`);
      }
    });
    
    this.manager.events.addUserLoaded((user) => {
      //console.log('User loaded event');
      this.userSubject.next(user);
      
      if (this.renewalInProgress) {
        //console.log('Silent renew succeeded');
        this.renewalInProgress = false;
        this.renewalAttempts = 0;
      }
      
      // Start the renewal timer
      this.startTokenRenewalTimer();
    });
    
    // Listen for messages from silent-redirect.html
    window.addEventListener('message', (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data && event.data.type === 'situ_auth_error') {
        //console.log('Received auth error from silent redirect:', event.data.error);
        if (event.data.error === 'login_required') {
          // Handle session expiration - could show login prompt
          this.renewalInProgress = false;
        }
      }
    });
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
    
    // Clear any renewal timer
    this.clearRenewalTimer();
  }

  get roles() {
    return this.user?.profile?.role ?? [];
  }

  isLoggedIn(): boolean {
    return this.user != null && !this.user.expired;
  }

  getClaims(): any {
    return this.user?.profile;
  }

  getClaim(claim: string): string {
    return this.user?.profile[claim] ?? null;
  }

  getAuthorizationHeaderValue(): string {
    return `${this.user?.token_type} ${this.user?.access_token}`;
  }

  logout(): Promise<void> {
    return this.manager.signoutRedirect();
  }

  startAuthentication(returnPath: string): Promise<void> {
    return this.manager.signinRedirect({ state: { returnPath } });
  }

  createSigninRequest(): Promise<SigninRequest> {
    return this.manager.createSigninRequest();
  }

  // Updated signInSilent method with circuit breaker and concurrency protection
  signInSilent(): Promise<User | null> {
    // Circuit breaker to prevent hammering
    const now = Date.now();
    const timeSinceLastRequest = now - AuthService.lastTokenRequestTime;
    
    if (timeSinceLastRequest < AuthService.MIN_REQUEST_INTERVAL) {
      AuthService.tokenRequestCount++;
      
      // If we detect rapid requests, block temporarily
      if (AuthService.tokenRequestCount > 3) {
        console.error(`Too many token requests in a short period (${AuthService.tokenRequestCount} in ${timeSinceLastRequest}ms)`);
        return Promise.reject(new Error('Rate limited: Too many token requests'));
      }
    } else {
      // Reset counter if sufficient time has passed
      AuthService.tokenRequestCount = 0;
    }
    
    AuthService.lastTokenRequestTime = now;
    
    // Prevent concurrent renewal attempts
    if (this.renewalInProgress) {
      //console.log('Token renewal already in progress, skipping duplicate request');
      return Promise.resolve(this.user);
    }
    
    this.renewalInProgress = true;
    //console.log('Initiating silent sign-in');
    
    return this.manager.signinSilent()
      .then(user => {
        this.renewalInProgress = false;
        return user;
      })
      .catch(error => {
        console.error('Silent sign-in failed', error);
        // Note: The addSilentRenewError event handler will handle retry logic
        // Just clean up the flag here
        this.renewalInProgress = false;
        throw error;
      });
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback()
    .then(user => {
      this.userSubject.next(user);

      if (user?.state?.returnPath) {
        this.router.navigateByUrl(user.state.returnPath);
      }
    });
  }

  completeViewAsAuthentication(url?: string): Promise<void> {
    return this.manager.signinRedirectCallback(url).then(user => {
      this.userSubject.next(user);
    });
  }

  isAuthorized(roles: Role[]) {
    // must be authenticated
    if (!this.user) {
      return false;
    }

    // no roles required
    if (!roles || !roles.length) {
      return true;
    }

    if (this.hasFullAccess()) {
      return true;
    }

    // check if user has any of the required roles
    if (roles.some(role => this.hasRole(role))) {
      return true;
    }

    // unauthorized
    return false;
  }

  getUserName() {
    return this.getClaim('name') ?? this.getClaim('email');
  }

  getUserId() {
    return this.getClaim('sub');
  }

  getOriginalUserId() {
    return this.getClaim('original_user_id');
  }

  getDefaultCompanyCode(): string | null {
    var defaultCompany = this.getDefaultCompany();
    if (!defaultCompany) {
      return this.getClaim("builder");
    }

    return defaultCompany?.code ?? null;
  }

  getDefaultCompanyName(): string | null {
    var defaultCompany = this.getDefaultCompany();
    if (!defaultCompany) {
      return this.getClaim("company_name");
    }

    return defaultCompany?.name ?? null;
  }

  getDefaultCompanyId(): string | null {
    var defaultCompany = this.getDefaultCompany();
    if (!defaultCompany) {
      return this.getClaim("company_id");
    }

    return defaultCompany?.id ?? null;
  }

  getDefaultCompany(): Company | null {
   return this.listCompanies()?.find(x => x.id == this.getClaim("default_company_id")) ?? null;
  }

  listCompanies(): Company[] {
    let companyClaim = this.getClaim("company");
    if (!companyClaim) {
      return [];
    }
    let jsonCompanies = Array.isArray(companyClaim) ? companyClaim as string[] : [companyClaim];
    let sorted =  jsonCompanies.map(x => JSON.parse(x) as Company);
    return sorted.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
  }

  isAuthorizedForCompanyById(companyId: string): boolean {
    // must be authenticated
    if (!this.user) {
      return false;
    }

    // situ admin/dev have access to all companies
    if (this.hasRole(Role.SituSystemsAdmin) || this.hasRole(Role.SituSystemsDeveloper)) {
      return true;
    }

    let companies = this.listCompanies();
    if (companies.some(x => x.id == companyId)) {
      return true;
    }

    // unauthorized
    return false;
  }

  isAuthorizedForCompanyByCode(companyCode: string): boolean {
    // must be authenticated
    if (!this.user) {
      return false;
    }

    // situ admin/dev have access to all companies
    if (this.hasRole(Role.SituSystemsAdmin) || this.hasRole(Role.SituSystemsDeveloper)) {
      return true;
    }

    let companies = this.listCompanies();
    if (companies.some(x => x.code?.toLowerCase() == companyCode?.toLowerCase())) {
      return true;
    }

    // unauthorized
    return false;
  }

  hasFullAccess() {
    // must be authenticated
    if (!this.user) {
      return false;
    }

    // these roles have full access
    if (this.hasRole(Role.SituSystemsAdmin) || this.hasRole(Role.SituSystemsDeveloper)) {
      return true;
    }

    return false;
  }

  hasDeveloperAccess() {
    // must be authenticated
    if (!this.user) {
      return false;
    }

    if (this.hasRole(Role.SituSystemsDeveloper)) {
      return true;
    }

    return false;
  }

  hasSituAccess() {
    // must be authenticated
    if (!this.user) {
      return false;
    }

    // these roles have full access
    if (this.hasRole(Role.SituSystemsAdmin) || this.hasRole(Role.SituSystemsDeveloper || this.hasRole(Role.SituSystemsDeveloper))) {
      return true;
    }

    return false;
  }

  hasRole(role: Role) {
    return this.roles.includes(role);
  }

  hasAllRoles(roles: Role[]) {
    return roles.every(x => this.hasRole(x));
  }

  hasAnyRole(roles: Role[]) {
    return roles.some(x => this.hasRole(x));
  }

  private startTokenRenewalTimer(): void {
    // Clear any existing timer
    this.clearRenewalTimer();
    
    if (!this.user) return;
    
    // Calculate when to renew (e.g., when token is 80% through its lifetime)
    const expiresAt = this.user.expires_at || 0;
    const now = Math.floor(Date.now() / 1000);
    const tokenLifetimeSec = Math.max(expiresAt - now, 0);
    const renewalTimeMs = tokenLifetimeSec * 0.8 * 1000;
    
    //console.log(`Token expires in ${tokenLifetimeSec} seconds, scheduling renewal in ${renewalTimeMs/1000} seconds`);
    
    // Set the timer
    this.renewalTimer = setTimeout(() => {
      //console.log('Scheduled time reached, renewing token');
      this.signInSilent().catch(err => console.error('Scheduled token renewal failed', err));
    }, renewalTimeMs);
  }

  private clearRenewalTimer(): void {
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
  }
}
